import apiClient from './apiClient';
import { getApiBaseUrl } from './apiClient';
import cadrageService from './cadrageService';
import useCaseService from './useCaseService';

const apiBaseURL = '/documents/';

export interface DocumentRecord {
  id: string;
  name?: string;
  folder_path?: string;
  minio_path?: string;
  minioPath?: string;
  mime_type?: string;
  mimeType?: string;
  size?: number;
  created_at?: string;
  updated_at?: string;
  owner_id?: string;
  domain_id?: string;
  cadrage_id?: string;
  use_case_id?: string;
  chunks_count?: number;
  indexed_items_count?: number;
  indexing_status?: string;
  indexed_at?: string;
  theme_codes?: string[];
  /** Knowledge visibility (lot K): 'org' = whole organization, 'private' = owner only. */
  visibility?: DocumentVisibility;
  [key: string]: unknown;
}

export type DocumentVisibility = 'org' | 'private';

/** Backend folder listing: prefix, subfolders, files */
export interface FolderListingResponse {
  prefix: string;
  subfolders: Array<{ name: string; type: 'folder' | 'file' }>;
  files: Array<{ name: string; type: 'folder' | 'file' }>;
}

export const documentService = {
  /**
   * List documents for a cadrage (optionally by folder_path)
   */
  async listByCadrage(cadrageId: string, params?: { folder_path?: string }): Promise<DocumentRecord[]> {
    const response = await apiClient.get<DocumentRecord[]>(`${apiBaseURL}cadrages/${cadrageId}`, { params });
    return response.data;
  },

  /**
   * List documents for a domain (optionally by folder_path)
   */
  async listByDomain(domainId: string, params?: { folder_path?: string }): Promise<DocumentRecord[]> {
    const response = await apiClient.get<DocumentRecord[]>(`${apiBaseURL}domains/${domainId}`, { params });
    return response.data;
  },

  /**
   * Alias for listByDomain (for consistency)
   */
  async listForDomain(domainId: string, params?: { folder_path?: string }): Promise<DocumentRecord[]> {
    return this.listByDomain(domainId, params);
  },

  /**
   * Alias for listByCadrage (for consistency)
   */
  async listForCadrage(cadrageId: string, params?: { folder_path?: string }): Promise<DocumentRecord[]> {
    return this.listByCadrage(cadrageId, params);
  },

  /**
   * List documents for a use case.
   * Kept method name for backward compatibility in callers.
   */
  async listByAudit(auditId: string, params?: any): Promise<DocumentRecord[]> {
    const response = await apiClient.get<DocumentRecord[]>(`${apiBaseURL}use-cases/${auditId}`, { params });
    return response.data;
  },

  /**
   * Upload a document to a cadrage
   */
  async uploadToCadrage(cadrageId: string, formData: FormData): Promise<DocumentRecord> {
    const response = await apiClient.post(`${apiBaseURL}cadrages/${cadrageId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Upload a document to a domain
   */
  async uploadToDomain(domainId: string, formData: FormData): Promise<DocumentRecord> {
    const response = await apiClient.post(`${apiBaseURL}domains/${domainId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Upload a file to a domain (convenience method)
   */
  async uploadForDomain(domainId: string, file: File, folderPath = '/'): Promise<DocumentRecord> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_path', folderPath);
    return this.uploadToDomain(domainId, formData);
  },

  /**
   * Upload a file to a cadrage (convenience method)
   */
  async uploadForCadrage(cadrageId: string, file: File, folderPath = '/'): Promise<DocumentRecord> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_path', folderPath);
    return this.uploadToCadrage(cadrageId, formData);
  },

  /**
   * Upload a document to a use case.
   * Kept method name for backward compatibility in callers.
   */
  async uploadToAudit(auditId: string, formData: FormData): Promise<DocumentRecord> {
    const response = await apiClient.post(`${apiBaseURL}use-cases/${auditId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Delete a document by ID
   */
  async delete(documentId: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}${documentId}`);
  },

  /**
   * "Share with the organization" switch of a domain, use case or solution document.
   * Folder documents follow their folder (see customFolderService.setSharing).
   */
  async setVisibility(documentId: string | number, visibility: DocumentVisibility): Promise<DocumentRecord> {
    const response = await apiClient.put<DocumentRecord>(`${apiBaseURL}${documentId}/visibility`, { visibility });
    return response.data;
  },

  /**
   * Get a presigned download URL for a document
   */
  async getDownloadUrl(documentId: string): Promise<string> {
    const response = await apiClient.get<{ url: string }>(`${apiBaseURL}${documentId}/download`);
    return response.data.url;
  },

  /**
   * Path to stream by document ID (correct bucket from backend). Prefer this when documentId is available.
   */
  getStreamPathByDocumentId(documentId: string, disposition: 'inline' | 'attachment' = 'inline'): string {
    return `/documents/${documentId}/stream?disposition=${disposition}`;
  },

  /**
   * Get stream URL for preview/download. Prefer getStreamPath + apiClient.get(responseType:'blob')
   * so the request is authenticated and avoids MinIO SignatureDoesNotMatch when opening presigned URLs.
   */
  getStreamUrl(minioPath: string, disposition: 'inline' | 'attachment' = 'inline'): string {
    const base = getApiBaseUrl().replace(/\/$/, '');
    return `${base}/documents/stream?object_name=${encodeURIComponent(minioPath)}&disposition=${disposition}`;
  },

  /**
   * Path for stream endpoint (use with apiClient.get for authenticated fetch + blob).
   */
  getStreamPath(minioPath: string, disposition: 'inline' | 'attachment' = 'inline'): string {
    return `/documents/stream?object_name=${encodeURIComponent(minioPath)}&disposition=${disposition}`;
  },

  /**
   * List folders and files for a cadrage (MinIO listing)
   */
  async listFoldersByCadrage(cadrageId: string, params?: { prefix?: string }): Promise<FolderListingResponse> {
    return cadrageService.listFolders(cadrageId, params?.prefix);
  },

  /**
   * List folders and files for a use case (MinIO listing)
   */
  async listFoldersByUseCase(useCaseId: string, params?: { prefix?: string }): Promise<FolderListingResponse> {
    return useCaseService.listFolders(useCaseId, params?.prefix);
  },

  // ========== Custom Folder Methods ==========

  /**
   * List documents in a custom knowledge base folder
   */
  async listForCustomFolder(folderId: string, params?: { folder_path?: string }): Promise<DocumentRecord[]> {
    const response = await apiClient.get(`${apiBaseURL}custom-folders/${folderId}`, { params });
    return response.data;
  },

  /**
   * Upload a file to a custom folder
   */
  async uploadToCustomFolder(folderId: string, file: File, folderPath = '/'): Promise<DocumentRecord> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_path', folderPath);
    
    const response = await apiClient.post(`${apiBaseURL}custom-folders/${folderId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Download a document as a blob.
   * Handles authenticated requests with blob response type.
   * 
   * @param path - Stream path (use getStreamPath or getStreamPathByDocumentId)
   * @returns Blob data
   */
  async downloadDocument(path: string): Promise<Blob> {
    const response = await apiClient.get(path, { responseType: 'blob' });
    return response.data;
  },
};

export default documentService;
